import { useState, useCallback } from 'react';
import { notification } from 'antd';
import { useSelector } from 'react-redux';
import {
  getChatAssistantDrafts,
  createChatAssistantDraft,
  deleteChatAssistantDraft,
  getChatDraftWithMessages,
} from '../../../API/services/screeningService';
import { t } from '../../../i18n/i18n';
import { ERROR_STATUSES } from '../../../constants/chatAssistant.constants';

export const useChatDraftManagement = () => {
  const [chatDrafts, setChatDrafts] = useState([]);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [isDeletingDraft, setIsDeletingDraft] = useState(false);
  const [pendingSavePromises, setPendingSavePromises] = useState([]);
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);

  const { currentWorkspace } = useSelector((state) => state.auth);

  const loadChatDrafts = useCallback(async () => {
    try {
      setLoadingDrafts(true);
      const response = await getChatAssistantDrafts();
      setChatDrafts(response.collection || []);
    } catch (error) {
      notification.error({
        message: t('ERROR'),
        description: t('ERROR_LOADING_ASSISTANT_DRAFTS'),
      });
    } finally {
      setLoadingDrafts(false);
    }
  }, []);

  const createNewDraft = useCallback(
    async (threadId, name, messages = []) => {
      if (!threadId || threadId === 'null' || threadId === 'undefined' || threadId === '') {
        notification.error({
          message: t('CHAT_ERROR_INVALID_THREAD'),
        });
        return null;
      }

      try {
        const response = await createChatAssistantDraft(currentWorkspace, threadId, name, messages);
        const draftData = response.result || response;

        const newDraft = {
          id: draftData.id,
          name: draftData.name,
          threadId: draftData.threadId || threadId,
          createdAt: draftData.createdAt || new Date().toISOString(),
          updatedAt: draftData.updatedAt || new Date().toISOString(),
          percentage: 0,
          workspaceId: currentWorkspace,
        };

        setChatDrafts((prevDrafts) => [newDraft, ...prevDrafts]);
        return draftData.id;
      } catch (error) {
        notification.error({
          message: t('CHAT_ERROR_CREATE_DRAFT'),
        });
        return null;
      }
    },
    [currentWorkspace]
  );

  const handleDeleteDraftClick = useCallback((draftId, threadId) => {
    setDraftToDelete({ id: draftId, threadId });
    setShowDeleteModal(true);
  }, []);

  const deleteDraft = useCallback(
    async (draftId, onDraftDeleted) => {
      try {
        setIsDeletingDraft(true);
        await deleteChatAssistantDraft(draftId);
        await loadChatDrafts();

        if (onDraftDeleted) {
          onDraftDeleted(draftId);
        }

        setShowDeleteModal(false);
        setDraftToDelete(null);

        notification.success({
          message: t('CHAT_DRAFT_DELETED'),
        });
      } catch (error) {
        notification.error({
          message: t('CHAT_ERROR_DELETE_DRAFT'),
        });
      } finally {
        setIsDeletingDraft(false);
      }
    },
    [loadChatDrafts]
  );

  const loadDraftWithMessages = useCallback(async (draftId) => {
    try {
      const draftWithMessages = await getChatDraftWithMessages(draftId);
      const messages = draftWithMessages.messages || [];
      const percentageValue = draftWithMessages.percentage;
      const customTestGroup = draftWithMessages.customTestGroup;
      const subskills = draftWithMessages.subskills;
      const evaluationMetadata = draftWithMessages.evaluationMetadata;

      return {
        messages,
        percentage: percentageValue,
        customTestGroup,
        subskills,
        evaluationMetadata,
        evaluationDraft: draftWithMessages.evaluationDraft,
        evaluationOpened: draftWithMessages.evaluationOpened,
        screeningId: draftWithMessages.screeningId,
      };
    } catch (error) {
      if (error.response?.status === ERROR_STATUSES.NOT_FOUND || error.message?.includes('404')) {
        notification.info({
          message: t('CHAT_INFO_THREAD_NOT_FOUND'),
          description: t('CHAT_INFO_THREAD_NOT_FOUND_DESC'),
        });
        return {
          messages: [],
          percentage: null,
          customTestGroup: null,
          subskills: null,
          evaluationMetadata: null,
          evaluationDraft: null,
          evaluationOpened: false,
          screeningId: null,
        };
      } else {
        notification.warning({
          message: t('CHAT_WARNING_LOAD_MESSAGES'),
          description: error.message,
        });
        throw error;
      }
    }
  }, []);

  const addPendingSavePromise = useCallback((promise) => {
    setPendingSavePromises((prev) => [...prev, promise]);
    promise.finally(() => {
      setPendingSavePromises((prev) => prev.filter((p) => p !== promise));
    });
  }, []);

  const waitForPendingSaves = useCallback(async () => {
    if (pendingSavePromises.length > 0) {
      try {
        await Promise.allSettled(pendingSavePromises);
      } catch (error) {
        console.error('Error waiting for pending saves:', error);
      }
    }
  }, [pendingSavePromises]);

  const hasPendingSaves = pendingSavePromises.length > 0;

  return {
    chatDrafts,
    currentDraftId,
    loadingDrafts,
    showDeleteModal,
    draftToDelete,
    isDeletingDraft,
    isLoadingDraft,
    hasPendingSaves,
    setChatDrafts,
    setCurrentDraftId,
    setShowDeleteModal,
    setDraftToDelete,
    setIsLoadingDraft,
    loadChatDrafts,
    createNewDraft,
    handleDeleteDraftClick,
    deleteDraft,
    loadDraftWithMessages,
    addPendingSavePromise,
    waitForPendingSaves,
  };
};
